import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import { ADD, EDIT, IDLE, PENDING, PUBLISHED } from "const"
import { hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_RRS_TEMPLATE,
    StoreType,
    RrsCategoryList,
    RrsTemplateProps,

    LoadAllRrsCategories,
    LoadAllRrsTemplateLabels,
    LoadAllRrsTemplateLabelCategories,
    LoadAllRrsTemplateLevels,
    LoadAllRrsTemplateRiskRanks,
    LoadRrsTemplateById,
    IsLockedRrsTemplate,
    SortRrsCategories,
    PrepareRrsCategoryNestedIds,

    setRrsCategoryList,
    setRrsCategoryChildParent,
    setRrsCategoryIds,
    setRrsTemplateLabelList,
    setRrsTemplateLabelCategoryList,
    setRrsTemplateLevelList,
    setRrsTemplateRiskRankList,

    setRrsTemplateCategoriesCategoryList,
    setRrsTemplateCategoriesCategoryChildParent,

    setRrsTemplateEdit,
    setRrsTemplateEditLoadState,
    setRrsTemplateReloadEdit,
    initRrsTemplate,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    TableNestedIds,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"
import TemplateLabelList from "../labelList"
import RiskMap from "../riskMap"

const TemplateEdit = ({
    templateId = null,
}: RrsTemplateProps) => {
    const template = useSelector((state: StoreType) => state.rrsTemplate.templateEdit)
    const editLoadState = useSelector((state: StoreType) => state.rrsTemplate.editLoadState)
    const reloadEdit = useSelector((state: StoreType) => state.rrsTemplate.reloadEdit)

    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    useEffect(() => {
        const promiseArray: Promise<any>[] = [
            LoadAllRrsCategories(userData.accountId, project.id).then((res) => {
                const categoriesFiltered = SortRrsCategories(Object.values(res.categoryList), {})
                const groupedIds = PrepareRrsCategoryNestedIds(categoriesFiltered)

                dispatch(setRrsCategoryList(res.categoryList))
                dispatch(setRrsCategoryChildParent(res.childParent))
                dispatch(setRrsCategoryIds(groupedIds))
            }),
            LoadAllRrsTemplateLabels(templateId).then((labels) => {
                dispatch(setRrsTemplateLabelList(labels))
                const nestedPromiseArray: Promise<any>[] = []

                labels.forEach((label) => {
                    nestedPromiseArray.push(
                        LoadAllRrsTemplateLabelCategories(label.id).then((nestedCategories) => {
                            dispatch(setRrsTemplateLabelCategoryList(label.id, nestedCategories))
                        }),
                        LoadAllRrsTemplateLevels(label.id).then((levels) => {
                            dispatch(setRrsTemplateLevelList(label.id, levels))
                        }),
                    )
                })

                return Promise.all(nestedPromiseArray)
            }),
            LoadAllRrsTemplateRiskRanks(templateId).then((res) => {
                dispatch(setRrsTemplateRiskRankList(res))
            }),
        ]

        // "select categories" for levels, sorting and preparing nested ids is on "SelectCategory" level
        if (accountSettings.rrs.defaultCategoriesAssigned) {
            promiseArray.push(
                LoadAllRrsCategories(userData.accountId, null).then((res) => {
                    dispatch(setRrsTemplateCategoriesCategoryList(res.categoryList))
                    dispatch(setRrsTemplateCategoriesCategoryChildParent(res.childParent))
                }),
            )
        } else {
            promiseArray.push(
                LoadAllRrsCategories(1, null).then((res) => {
                    const groupedIds = PrepareRrsCategoryNestedIds(Object.values(res.categoryList))
                    let nestedIds = groupedIds.length ? groupedIds[0].nestedIds : []

                    // show only published from Super Admin
                    nestedIds = nestedIds.filter((row) => {
                        const category = res.categoryList[Number(row.id)]
                        return category.status === PUBLISHED
                    })

                    const publishedCategoryList: RrsCategoryList = {}
                    const addToList = (nested: readonly TableNestedIds[]) => {
                        nested.forEach((row) => {
                            const id = Number(row.id)
                            publishedCategoryList[id] = res.categoryList[id]
                            addToList(row.nestedIds)
                        })
                    }
                    addToList(nestedIds)

                    dispatch(setRrsTemplateCategoriesCategoryList(publishedCategoryList))
                    dispatch(setRrsTemplateCategoriesCategoryChildParent(res.childParent))
                }),
            )
        }

        if (templateId === 0) {
            dispatch(setRrsTemplateEdit(initRrsTemplate))
        } else {
            promiseArray.push(
                LoadRrsTemplateById(templateId, userData.accountId, project.id).then((res) => {
                    dispatch(setRrsTemplateEdit(res))
                }),
            )
        }

        dispatch(setRrsTemplateEditLoadState(PENDING))
        Promise.all(promiseArray).then(() => {
            dispatch(setRrsTemplateEditLoadState(IDLE))

            dispatch(setRrsTemplateReloadEdit(false))
            setTimeout(() => {
                dispatch(setRrsTemplateReloadEdit(true))
            }, 0)
        })
    }, [])

    const addAccess = !project.id
        ? hasAccess(PRM_RRS_TEMPLATE, ADD)
        : hasProjectAccess(PRM_RRS_TEMPLATE, ADD)

    const editAccess = !project.id
        ? hasAccess(PRM_RRS_TEMPLATE, EDIT)
        : hasProjectAccess(PRM_RRS_TEMPLATE, EDIT)

    if (templateId === 0 && !addAccess) return <AccessDenied />
    if (templateId !== 0 && !editAccess) return <AccessDenied />
    if (!template) return <NotFound />

    const isLocked = IsLockedRrsTemplate(project.id, template)

    return (
        <Container>
            <PageHeader>
                <PageTitle>{template.id ? `${lang.editTemplate} # ${template.id}` : lang.addTemplate}</PageTitle>
            </PageHeader>
            <PageContent>
                {editLoadState === PENDING && <Loader />}
                {
                    !reloadEdit
                    && <Form template={template} />
                }
                {
                    reloadEdit
                    && <Form template={template} />
                }
                {
                    template.id
                    && <TemplateLabelList editAccess={!isLocked} />
                }
                {
                    template.id
                    && <RiskMap templateId={templateId} editAccess={!isLocked} />
                }
            </PageContent>
        </Container>
    )
}
export default TemplateEdit
